import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { MessageCircle, Mail, MapPin, CalendarDays, Send } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/motion";
import { CONTACT } from "@/data/site";
import { LogoMark } from "@/components/Logo";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROWS = [
  {
    icon: MessageCircle,
    label: "Telefon & WhatsApp",
    value: CONTACT.phone,
    href: CONTACT.whatsapp,
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACT.email,
    href: CONTACT.emailHref,
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Portul de origine",
    value: CONTACT.location,
    href: null,
    testid: "contact-location",
  },
  {
    icon: CalendarDays,
    label: "Sezon",
    value: "Mai — Octombrie 2027 · rezervări deschise",
    href: null,
    testid: "contact-season",
  },
];

const EXPERIENCE_OPTIONS = [
  { value: "apus-de-soare", label: "Apus de Soare" },
  { value: "excursii-balcik", label: "Excursii Balcik" },
  { value: "excursii-varna", label: "Excursii Varna" },
  { value: "team-building", label: "Team Building" },
  { value: "nunti-pe-velier", label: "Nunți pe Velier" },
  { value: "training-skipper", label: "Training Skipper" },
  { value: "croaziere-pe-mare", label: "Croaziere pe Mare" },
  { value: "regate-2027", label: "Regate 2027" },
  { value: "nesigur", label: "Nu m-am hotărât încă" },
];

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  experience: "apus-de-soare",
  date: "",
  guests: "",
  message: "",
};

const inputClass =
  "w-full border border-white/10 bg-abyss px-4 py-3 text-sm text-ivory placeholder:text-mist/50 outline-none transition-colors duration-300 focus:border-gold";

const SEASON_START = new Date(2027, 4, 1);
const SEASON_END = new Date(2027, 9, 31);

const BookingForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then((res) => setBooked(res.data.booked || []))
      .catch(() => {});
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        experience: form.experience,
        date: form.date || null,
        guests: form.guests ? Number(form.guests) : null,
        message: form.message || null,
      });
      toast.success("Cererea a fost trimisă! Te contactăm în aceeași zi.");
      if (form.date) setBooked((prev) => [...prev, form.date]);
      setForm(EMPTY_FORM);
    } catch {
      toast.error(`Ceva nu a mers. Sună-ne direct la ${CONTACT.phone}.`);
    } finally {
      setSending(false);
    }
  };

  const selectedDate = form.date ? new Date(`${form.date}T00:00:00`) : undefined;

  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <input
        required
        value={form.name}
        onChange={set("name")}
        placeholder="Nume și prenume *"
        data-testid="booking-name-input"
        className={inputClass}
      />
      <input
        required
        value={form.phone}
        onChange={set("phone")}
        placeholder="Telefon *"
        type="tel"
        data-testid="booking-phone-input"
        className={inputClass}
      />
      <input
        value={form.email}
        onChange={set("email")}
        placeholder="Email (opțional)"
        type="email"
        data-testid="booking-email-input"
        className={inputClass}
      />
      <select
        value={form.experience}
        onChange={set("experience")}
        data-testid="booking-experience-select"
        className={`${inputClass} appearance-none`}
      >
        {EXPERIENCE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-abyss">
            {opt.label}
          </option>
        ))}
      </select>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="booking-date-input"
            className={`${inputClass} flex items-center gap-3 text-left ${selectedDate ? "text-ivory" : "text-mist/50"}`}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-gold" />
            {selectedDate
              ? format(selectedDate, "d MMMM yyyy", { locale: ro })
              : "Alege data din calendar"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto border-white/10 bg-deep p-0" align="start">
          <Calendar
            mode="single"
            locale={ro}
            selected={selectedDate}
            onSelect={(day) =>
              setForm({ ...form, date: day ? format(day, "yyyy-MM-dd") : "" })
            }
            disabled={[
              { before: SEASON_START, after: SEASON_END },
              ...booked.map((b) => new Date(`${b}T00:00:00`)),
            ]}
            defaultMonth={SEASON_START}
            fromMonth={SEASON_START}
            toMonth={SEASON_END}
            data-testid="booking-calendar"
          />
        </PopoverContent>
      </Popover>
      <input
        value={form.guests}
        onChange={set("guests")}
        placeholder="Număr persoane"
        type="number"
        min="1"
        max="50"
        data-testid="booking-guests-input"
        className={inputClass}
      />
      <p className="text-xs leading-relaxed text-mist sm:col-span-2" data-testid="booking-calendar-note">
        Zilele deja rezervate apar dezactivate în calendar. Sezonul 2027: mai — octombrie.
      </p>
      <textarea
        value={form.message}
        onChange={set("message")}
        placeholder="Mesaj — spune-ne ce ai în plan (opțional)"
        rows={4}
        data-testid="booking-message-input"
        className={`${inputClass} resize-none sm:col-span-2`}
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="booking-submit-button"
        className="flex items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-normal uppercase tracking-[0.25em] text-abyss transition-colors duration-300 hover:bg-gold-hover disabled:opacity-50 sm:col-span-2"
      >
        <Send className="h-4 w-4" />
        {sending ? "Se trimite..." : "Trimite Cererea"}
      </button>
      <p className="text-xs leading-relaxed text-mist sm:col-span-2" data-testid="booking-gdpr-note">
        Prin trimiterea formularului ești de acord cu prelucrarea datelor tale
        exclusiv pentru procesarea cererii, conform{" "}
        <Link to="/politica-confidentialitate" className="text-gold underline underline-offset-4">
          Politicii de Confidențialitate
        </Link>
        .
      </p>
    </form>
  );
};

const Contact = () => (
  <div data-testid="contact-page" className="pt-20">
    <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
      <div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <Overline className="mb-8">Contact & Rezervări</Overline>
        </motion.div>
        <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.3}>Să</MaskedLine>
          <MaskedLine delay={0.45} className="italic text-gold">
            Navigăm.
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 max-w-md text-base leading-relaxed text-mist"
        >
          Un telefon ajunge. Spune-ne ce fel de zi îți dorești pe mare — nuntă,
          curs, apus sau o săptămână întreagă — și îți răspundem în aceeași zi
          cu toate detaliile.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-16 hidden lg:block"
        >
          <LogoMark className="h-16 w-16 text-gold/30" />
        </motion.div>
      </div>

      <div className="flex flex-col justify-center">
        {ROWS.map((row, i) => {
          const inner = (
            <div className="group flex items-center gap-6 border-t border-white/10 py-8">
              <row.icon className="h-5 w-5 shrink-0 text-gold" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-mist">{row.label}</p>
                <p className={`mt-2 font-serif text-2xl font-light sm:text-3xl ${row.href ? "text-ivory transition-colors duration-300 group-hover:text-gold" : "text-ivory"}`}>
                  {row.value}
                </p>
              </div>
            </div>
          );
          return (
            <Reveal key={row.label} delay={0.2 + i * 0.1}>
              {row.href ? (
                <a href={row.href} target="_blank" rel="noopener noreferrer" data-testid={row.testid}>{inner}</a>
              ) : (
                <div data-testid={row.testid}>{inner}</div>
              )}
            </Reveal>
          );
        })}
        <Reveal delay={0.6}>
          <div className="border-t border-white/10 pt-8">
            <p className="text-sm leading-relaxed text-mist">
              Pentru nunți și evenimente, recomandăm rezervarea cu 3–6 luni
              înainte. Pentru apusuri și cursuri, câteva săptămâni sunt de
              obicei suficiente.
            </p>
          </div>
        </Reveal>
      </div>
    </div>

    <section className="border-t border-white/10 bg-deep">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-32">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline className="mb-6">Formular de Rezervare</Overline>
            <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl">
              Completezi aici, <span className="italic text-gold">te sunăm noi.</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-mist">
              Cererea ajunge direct în inboxul nostru și îți răspundem în
              aceeași zi, cu disponibilitate și ofertă exactă. Fără cont, fără
              plată online — doar o conversație.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-8">
          <BookingForm />
        </Reveal>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="contact-map-section">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-32">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline className="mb-6">Cum Ajungi la Noi</Overline>
            <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl">
              Marina Life Harbour, <span className="italic text-gold">Limanu.</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-mist">
              La 10 minute de Mangalia și 45 de minute de Constanța, pe DJ391.
              Parcare în marina, pontonul nostru e la 2 minute de intrare —
              caută numele HOPE pe pupă.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-8">
          <div className="overflow-hidden border border-white/10">
            <iframe
              title="Harta Marina Life Harbour, Limanu"
              data-testid="contact-map-iframe"
              src="https://www.openstreetmap.org/export/embed.html?bbox=28.5100%2C43.7880%2C28.5750%2C43.8200&layer=mapnik&marker=43.8042%2C28.5422"
              className="h-[380px] w-full grayscale-[35%] invert-[92%] hue-rotate-180 contrast-[0.9] md:h-[460px]"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Contact;
